import React from 'react';
import { Bot, BarChart3, Shield, MessageSquare, Users, ChevronRight, Check } from 'lucide-react';
import Layout from '../../components/layout';

function VictorPage() {
    return (
        <Layout>

            <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
                {/* Hero Section */}
                <header className="container mx-auto px-4 py-16 md:py-24">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-12">
                        <div className="flex-1 space-y-6">
                            <div className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium">
                                Political Campaign Solution
                            </div>
                            <h1 className="text-4xl md:text-6xl font-bold text-gray-900">
                                Transform Your Political Campaign with Victor
                            </h1>
                            <p className="text-xl text-gray-600">
                                An intelligent WhatsApp chatbot that revolutionizes voter engagement and campaign analytics
                            </p>
                            <button className="flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors">
                                Get Started <ChevronRight size={20} />
                            </button>
                        </div>
                        <div className="flex-1">
                            <img
                                src="https://images.unsplash.com/photo-1540910419892-4a36d2c3266c?auto=format&fit=crop&q=80"
                                alt="Political Campaign"
                                className="rounded-lg shadow-2xl"
                            />
                        </div>
                    </div>
                </header>

                {/* Features Section */}
                <section className="bg-white py-20">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Key Features</h2>
                        <div className="grid md:grid-cols-3 gap-8">
                            <FeatureCard
                                icon={<Bot className="w-8 h-8 text-blue-600" />}
                                title="Smart Engagement"
                                description="Engage voters through natural conversations and multilingual support"
                            />
                            <FeatureCard
                                icon={<BarChart3 className="w-8 h-8 text-blue-600" />}
                                title="Real-time Analytics"
                                description="Get instant insights into voter sentiment and campaign performance"
                            />
                            <FeatureCard
                                icon={<Shield className="w-8 h-8 text-blue-600" />}
                                title="Secure & Compliant"
                                description="GDPR-compliant data handling with enterprise-grade security"
                            />
                        </div>
                    </div>
                </section>

                {/* Benefits Section */}
                <section className="py-20 bg-gray-50">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Why Choose Victor?</h2>
                        <div className="grid md:grid-cols-2 gap-8">
                            <BenefitCard
                                icon={<MessageSquare className="w-6 h-6 text-blue-600" />}
                                title="Automated Surveys"
                                description="Deploy targeted surveys with customizable questions to gather valuable voter insights"
                            />
                            <BenefitCard
                                icon={<Users className="w-6 h-6 text-blue-600" />}
                                title="Voter Engagement"
                                description="Build stronger connections with voters through personalized interactions"
                            />
                            <BenefitCard
                                icon={<BarChart3 className="w-6 h-6 text-blue-600" />}
                                title="Data-Driven Decisions"
                                description="Make informed campaign decisions based on real-time analytics and insights"
                            />
                            <BenefitCard
                                icon={<Shield className="w-6 h-6 text-blue-600" />}
                                title="Privacy First"
                                description="Ensure voter data protection with state-of-the-art security measures"
                            />
                        </div>
                    </div>
                </section>

                {/* Stats Section */}
                <section className="bg-blue-600 text-white py-16 mb-10">
                    <div className="container mx-auto px-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                            <StatCard number="70%" text="Survey Completion Rate" />
                            <StatCard number="20%" text="Sentiment Improvement" />
                            <StatCard number="24/7" text="Support Available" />
                            <StatCard number="100%" text="Data Security" />
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                {/* <section className="py-20">
                    <div className="container mx-auto px-4 text-center">
                        <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Campaign?</h2>
                        <p className="text-xl text-gray-600 mb-8">Get started with Victor today and revolutionize your voter engagement</p>
                        <button className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors">
                            Schedule a Demo
                        </button>
                    </div>
                </section> */}

                {/* Footer */}
                {/* <footer className="bg-gray-900 text-white py-12">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col md:flex-row justify-between items-center">
                            <div className="flex items-center gap-2 mb-4 md:mb-0">
                                <Bot size={24} />
                                <span className="text-xl font-bold">Victor</span>
                            </div>
                            <div className="flex gap-6">
                                <a href="#" className="hover:text-blue-400">About</a>
                                <a href="#" className="hover:text-blue-400">Features</a>
                                <a href="#" className="hover:text-blue-400">Contact</a>
                                <a href="#" className="hover:text-blue-400">Privacy</a>
                            </div>
                        </div>
                    </div>
                </footer> */}
            </div>
        </Layout>

    );
}

function FeatureCard({ icon, title, description }) {
    return (
        <div className="p-6 bg-white rounded-lg border border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
            <div className="mb-4">{icon}</div>
            <h3 className="text-xl font-semibold mb-2">{title}</h3>
            <p className="text-gray-600">{description}</p>
        </div>
    );
}

function BenefitCard({ icon, title, description }) {
    return (
        <div className="flex gap-4 p-6 bg-white rounded-lg shadow-md">
            <div className="flex-shrink-0">{icon}</div>
            <div>
                <h3 className="text-lg font-semibold mb-2">{title}</h3>
                <p className="text-gray-600">{description}</p>
            </div>
        </div>
    );
}

function StatCard({ number, text }) {
    return (
        <div>
            <div className="text-4xl font-bold mb-2">{number}</div>
            <div className="text-blue-100">{text}</div>
        </div>
    );
}

export default VictorPage;